import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, Timestamp, addDoc, collection } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../../firebase';
import usePortvilleRoomAuth from '../../../hooks/admin/usePortvilleRoomAuth';
import { PortvilleRoomWithResidency } from '../../../types/portvilleRoom';
import SignatureCanvas from 'react-signature-canvas';
import { setAdminScrollTop } from '../../../utils/adminScroll';
import { useBackToPortvilleRoomsListOnPop } from '../../../hooks/useBackToPortvilleRoomsListOnPop';
import { markPortvilleRoomsRestoreOnNext } from '../../../utils/portvilleRoomsScroll';
import { buildResidentNumber, sanitizeResidentNumberPart, splitResidentNumber } from '../../../utils/residentNumber';

// 연장 계약 종료일 계산
// 규칙: 연장 계약 시작일 기준 year + 2, day - 1 (day가 0이면 전월 말일)
const calculateTwoYearMinusOneEndDate = (startDate: Date) => {
  const year = startDate.getFullYear();
  const monthIndex = startDate.getMonth(); // 0-11
  const day = startDate.getDate(); // 1-31

  const endYear = year + 2;
  const endDay = day - 1;

  if (endDay === 0) return new Date(endYear, monthIndex, 0);
  return new Date(endYear, monthIndex, endDay);
};

const ExtensionContractPage: React.FC = () => {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loading: authLoading, hasAccess } = usePortvilleRoomAuth();
  
	  const [loading, setLoading] = useState(true);
	  const [room, setRoom] = useState<PortvilleRoomWithResidency | null>(null);
	  const [error, setError] = useState<string | null>(null);
	  const [extensionRound, setExtensionRound] = useState<1 | 2>(1);
	  const [signatureName, setSignatureName] = useState('');
	  const [signatureAddress, setSignatureAddress] = useState('');
	  const [signatureResidentNumberFront, setSignatureResidentNumberFront] = useState('');
	  const [signatureResidentNumberBack, setSignatureResidentNumberBack] = useState('');
	  const [signatureDate, setSignatureDate] = useState('');
	  const [saving, setSaving] = useState(false);
	  const [signatureSize, setSignatureSize] = useState({ width: 400, height: 200 });
  
  const sigCanvasRef = useRef<SignatureCanvas>(null);
  const signatureResidentNumber = buildResidentNumber(signatureResidentNumberFront, signatureResidentNumberBack);

  useBackToPortvilleRoomsListOnPop();

  const goToRoomsList = () => {
    markPortvilleRoomsRestoreOnNext();
    navigate('/admin/portville-rooms', { state: { restoreScroll: true } });
  };

  useEffect(() => {
    setAdminScrollTop(0);
  }, []);

  // 화면 크기에 따른 서명 패드 크기 조정
  const updateSignatureSize = useCallback(() => {
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      setSignatureSize({ width: 320, height: 160 });
    } else {
      setSignatureSize({ width: 400, height: 200 });
    }
  }, []);

  useEffect(() => {
    if (!authLoading && !hasAccess) {
      return;
    }
    
    if (roomId) {
      loadRoom();
    }
  }, [roomId, authLoading, hasAccess, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    updateSignatureSize();
    window.addEventListener('resize', updateSignatureSize);
    return () => window.removeEventListener('resize', updateSignatureSize);
  }, [updateSignatureSize]);

  // room 데이터가 로드되면 서명자 이름 기본값 설정
	  useEffect(() => {
	    if (room?.currentResident?.name && !signatureName) {
	      setSignatureName(room.currentResident.name);
	      console.log('✅ 서명자 성명 기본값 설정:', room.currentResident.name);
	    }
	  }, [room, signatureName]);

	  useEffect(() => {
	    if (!room) return;
	    if (room.signatureAddress && !signatureAddress) {
	      setSignatureAddress(room.signatureAddress);
	    }
	    if (room.signatureResidentNumber && !signatureResidentNumberFront && !signatureResidentNumberBack) {
        const residentNumberParts = splitResidentNumber(room.signatureResidentNumber);
	      setSignatureResidentNumberFront(residentNumberParts.front);
	      setSignatureResidentNumberBack(residentNumberParts.back);
	    }
	  }, [room, signatureAddress, signatureResidentNumberFront, signatureResidentNumberBack]);

	  const loadRoom = async () => {
	    if (!roomId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // 호실 정보 가져오기
      const roomRef = doc(db, 'portville_rooms', roomId);
      const roomDoc = await getDoc(roomRef);
      
      if (!roomDoc.exists()) {
        setError('호실 정보를 찾을 수 없습니다.');
        return;
      }
      
      const roomData = roomDoc.data();
      
      if (!roomData.currentResidencyId) {
        setError('현재 입주 기록이 없습니다.');
        return;
      }
      
      // 입주 기록 가져오기
      const residencyRef = doc(db, 'portville_residencies', roomData.currentResidencyId);
      const residencyDoc = await getDoc(residencyRef);
      
      if (!residencyDoc.exists()) {
        setError('입주 기록을 찾을 수 없습니다.');
        return;
      }
      
      const residencyData = residencyDoc.data();
      
      const requestedRound = searchParams.get('round') === '2' ? 2 : searchParams.get('round') === '1' ? 1 : null;
      const inferredRound = residencyData.secondExtensionStatus === 'approved' ? 2 : 1;
      const effectiveRound = requestedRound ?? inferredRound;

      if (effectiveRound === 2 && residencyData.hasExtended !== true) {
        setError('2차 연장 계약 날인은 1차 연장 완료 후 가능합니다.');
        return;
      }

      const approvalStatus = effectiveRound === 2 ? residencyData.secondExtensionStatus : residencyData.extensionStatus;
      if (approvalStatus !== 'approved') {
        setError(`${effectiveRound}차 계약 연장이 승인되지 않았습니다.`);
        return;
      }

      const alreadyContracted = effectiveRound === 2 ? residencyData.secondExtensionContractSignedAt : residencyData.extensionContractSignedAt;
      if (alreadyContracted) {
        setError(`이미 ${effectiveRound}차 연장 계약서 날인이 완료되었습니다.`);
        return;
      }

      setExtensionRound(effectiveRound);
      
	      const combinedRoom: PortvilleRoomWithResidency = {
	        id: roomData.id || roomId,
	        roomNumber: roomData.roomNumber,
	        roomType: roomData.roomType || '호실',
	        status: roomData.status,
	        currentResidencyId: roomData.currentResidencyId,
	        createdAt: roomData.createdAt?.toDate() || new Date(),
	        updatedAt: roomData.updatedAt?.toDate() || new Date(),
	        facilityInfo: roomData.facilityInfo,
	        
	                 // 입주 기록 정보
	         currentResident: residencyData.residentInfo,
	         residentUid: residencyData.residentUid,
	        occupancyPeriod: residencyData.occupancyPeriod ? {
	          startDate: residencyData.occupancyPeriod.startDate.toDate(),
	          endDate: residencyData.occupancyPeriod.endDate.toDate()
	        } : undefined,
	        signatureAddress: residencyData.signatureAddress,
	        signatureResidentNumber: residencyData.signatureResidentNumber,
	        extensionStatus: residencyData.extensionStatus,
	        extensionApprovedAt: residencyData.extensionApprovedAt?.toDate(),
	        originalEndDate: residencyData.originalEndDate?.toDate(),
	        hasExtended: residencyData.hasExtended,
	        hasSecondExtended: residencyData.hasSecondExtended,
        secondExtensionStatus: residencyData.secondExtensionStatus,
        secondExtensionApprovedAt: residencyData.secondExtensionApprovedAt?.toDate(),
        secondOriginalEndDate: residencyData.secondOriginalEndDate?.toDate()
      };
      
      setRoom(combinedRoom);
      
      // 서명 날짜 기본값 설정
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      setSignatureDate(`${year}-${month}-${day}`);
      
    } catch (error) {
      console.error('호실 정보 로드 실패:', error);
      setError('호실 정보를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

     const clearSignature = () => {
     if (sigCanvasRef.current) {
       sigCanvasRef.current.clear();
     }
   };

	  const saveExtensionContract = async () => {
	    if (
	      !room ||
	      !signatureName.trim() ||
	      !signatureAddress.trim() ||
	      !signatureResidentNumber ||
	      !signatureDate
	    ) {
	      alert('서명자 성명, 주소, 주민등록번호, 서명 날짜를 모두 입력해주세요.');
	      return;
	    }

      if (signatureResidentNumberFront.length !== 6 || signatureResidentNumberBack.length !== 7) {
        alert('주민등록번호 13자리를 확인해주세요.');
        return;
      }

         if (!sigCanvasRef.current || sigCanvasRef.current.isEmpty()) {
       alert('서명을 입력해주세요.');
       return;
     }

    try {
      setSaving(true);
      
             // 계약자 정보 (기존 입주자 정보 사용)
       const contractorInfo = {
         name: room.currentResident?.name || '',
         phoneNumber: room.currentResident?.phoneNumber || '',
         organization: room.currentResident?.organization || ''
       };
      
      // 사용자 프로필에서 추가 정보 가져오기 (필요시)
      if (room.residentUid && (!contractorInfo.phoneNumber || !contractorInfo.organization)) {
        try {
          const userProfileRef = doc(db, 'user_profiles', room.residentUid);
          const userProfileDoc = await getDoc(userProfileRef);
          
          if (userProfileDoc.exists()) {
            const userProfile = userProfileDoc.data();
            contractorInfo.phoneNumber = contractorInfo.phoneNumber || userProfile.phoneNumber || '';
            contractorInfo.organization = contractorInfo.organization || userProfile.organization || '';
          }
        } catch (profileError) {
          console.warn('사용자 프로필 조회 중 오류:', profileError);
        }
      }
      
      // 서명 이미지를 블롭으로 변환
      const canvas = sigCanvasRef.current.getCanvas();
      const blob = await new Promise<Blob>((resolve) => {
        canvas.toBlob((blob) => {
          resolve(blob!);
        }, 'image/png');
      });

      // Firebase Storage에 서명 이미지 업로드
      const signatureRef = ref(storage, `extension_contract_signatures/${roomId}_${Date.now()}.png`);
      await uploadBytes(signatureRef, blob);
      const signatureUrl = await getDownloadURL(signatureRef);

      const now = Timestamp.now();
      
      // 연장 계약 기간 계산
      const previousEndDate = room.occupancyPeriod?.endDate || new Date();
      // 연장 계약 시작일은 기존 계약 종료일 "다음날"
      const extensionStartDate = new Date(previousEndDate);
      extensionStartDate.setDate(extensionStartDate.getDate() + 1);
      const extensionEndDate = calculateTwoYearMinusOneEndDate(extensionStartDate);

      // 연장 계약서 내용 생성 (ContractSignaturePage.tsx의 계약 문구와 동일)
      const contractStartDate = extensionStartDate;
      const contractEndDate = extensionEndDate;

	      const extensionContractContent = {
	        signerInfo: {
	          name: signatureName,
	          address: signatureAddress,
	          residentNumber: signatureResidentNumber
	        },
	        header: `임대인 부산항만공사 사장 (이하 "갑" 이라 한다) 과 임차인 ${signatureName} (이하 "을" 이라 한다) 은 아래와 같이 임대차 계약을 체결하고 신의와 성실의 원칙에 입각하여 이를 이행한다.

소재지 : 부산광역시 강서구 신항로 96-87 (성북동 1501번지)
임대시설물: 부산 포트빌 ${room.roomNumber}호 (전용 26.5㎡ 1세대)`,
	        article1: `제1조 (목적)

갑은 위 표시 부산 포트빌 각 호를 을에게 임대하고 을은 이를 임차한다.`,
        article2: `제2조 (임대보증금 및 임대기간)

①임대보증금 및 임대료, 임대기간을 다음과 같이 약정한다.

임대보증금: 800,000원
월임대료: 135,000원
임대기간: ${contractStartDate.toLocaleDateString('ko-KR')} ~ ${contractEndDate.toLocaleDateString('ko-KR')}

②임대보증금은 임대계약 체결 후 가상계좌 발급 시 을이 갑에게 납부하여야 한다.
(신한은행 140-014-896398 예금주 : (주)아스타아이비에스 )

③을은 임대료를 매월 말일까지 갑이 지정하는 은행에 선납하여야 한다.
(신한은행 140-014-896099 예금주 : (주)아스타아이비에스 )

④입주자는 열쇠지급일이 기산점이다. 입주 당월 임대료 및 관리비는 일할 계산된다.

⑤임대기간 종료 전 을의 귀책사유로 인한 계약 해지 시, 을은 위약금으로 270,000원을 납부 또는 임대보증금에서 공제한다. 위약금의 납부·공제는 갑의 을에 대한 손해배상의 청구에 영향을 미치지 아니한다.`,
        article3: `제3조 (관리비)

①관리비는 일반관리비(청소, 경비, 영선 등 입주자 보호 및 건물유지보수비용)와 공공요금(전기료, 수도료, TV시청료, 환경개선분담금 등)을 말한다.

②매월 을은 일반관리비(세대당 86,500원, 부가세별도), 통신비(신청자에 한함) 및 공공요금을 납부하여야 한다.

③을은 갑이 매월 사용량 등에 따라 산정 고지한 금액의 관리비를 매월 말일까지 납부하여야 한다. (신한 140-014-896099 예금주: (주)아스타아이비에스)

④을이 계약기간 중 임대시설물을 사용하지 않을 경우 계약전력에 의한 기본요금을 납부하여야 한다.

⑤관리비는 갑이 지정한 시설관리사업자가 갑을 대신하여 산정·고지할 수 있으며, 이 경우 관리비는 갑이 지정한 시설관리사업자에게 납부하여야 한다.

⑥을은 입주 당월에 발생하는 관리비 선납부를 위하여 선관리비(100,000원)를 관리주체에 납부 예치하여야 하며, 퇴거 시 익월 말 공과금 및 시설물 보수비 등을 정산하고 잔액을 환불하며 이자는 산정치 아니한다. 다만 제7조에 따른 계약갱신 시에는 최초 납부한 선관리비는 그대로 예치한다.`,
        article4: `제4조 (연체료)

①을이 임대차계약에 의거 갑에게 납부하여야 할 임대료 및 관리비를 소정기일까지 납부하지 않을 때에는 국유재산법을 준용하여 가산금을 징수한다.

②체납임대료 납부 시에는 연체료를 우선 변제하며, 체납임대료는 완납 시까지 연체료를 가산하여 납부하여야 한다.`,
        article5: `제5조 (임대료 및 관리비 등 조정)

갑은 물가상승 및 기타 경제적 여건에 따라 임대료 및 관리비를 조정할 수 있으며 조정할 때에는 을에게 사전 통보하여야 한다.`,
        article6: `제6조 (의무·책임)

①갑은 이 계약서 내용을 성실히 이행할 의무가 있다.

②을은 세대내 모든 시설물을 주의하여 사용하여야하며, 사용부주의로 인한 파손, 망실 시에는 변상 또는 원상복구 하여야한다.

③을의 귀책사유로 인하여 갑 또는 제3자에게 인적 또는 물적 손해를 입혔을 때에는 을은 이를 변상함은 물론 민·형사상 모든 책임을 진다.

④을은 입주 시 입주자 관리카드를 제출하여야 하며 변경사항이 있을시 에는 관리실에 입주자 변경사항을 알려야 한다.

⑤을은 관리지침 및 포트빌 관리규약에 따라 임대시설을 이용해야 하며, 본 계약 및 지침, 규약을 이행하지 않을 경우 전적인 책임을 진다.`,
        article7: `제7조 (계약갱신)

을은 계약기간 만료 후 계속 거주를 희망하는 경우 만료 1개월 전에 갑에게 임대차계약의 갱신을 신청하여야 하며, 총 4년의 범위 내에서 2년 단위로 계약을 갱신할 수 있다. 단, 1회 연장 계약 후 계약 갱신은 입주대기자가 없는 경우 추가 갱신이 가능하다.`,
        article8: `제8조 (계약해지)

①갑은 을이 다음 각 호의 1에 해당하는 경우 임대차계약을 해지할 수 있다.
  1. 허가 없이 시설물구조를 변경하거나 건물 또는 시설물을 고의적으로 훼손한 세대
  2. 임대료 및 관리비등을 3개월 이상 체납한 세대
  4. 임대보증금의 반환청구권이 제3채권자의 질권 기타 채권담보의 목적이 되었을 때
  5. 질병 및 법정 전염병에 감염되어 타인에게 전염될 염려가 있는 자
  6. 음주 및 가무 등으로 풍기를 문란하게 하여 퇴거 경고를 3회 받은 세대
  7. 시설 내 질서와 안녕을 해칠 우려가 있어 퇴거 경고를 3회 받은 세대
  8. 기타 입주자격이 부적당하다고 갑이 인정한 세대
  9. 갑의 동의 없이 제3자에게 임대시설물 전부 또는 일부를 전대한 세대

②갑, 을이 어느 일방의 사정으로 계약 해지하고자 할 때에는 1개월 이전에 상대방에게 통보하여야 한다.

③을은 임대차계약 해지 신청 시 미납금 및 고지 예정인 관리비를 정산하여야 한다.`,
        article9: `제9조 (보증금의 반환)

을이 갑에게 예치한 임대보증금은 퇴거 시에(공휴일인 경우 익영업일)에 반환한다. 이때 을이 갑에게 납부하여야 할 월 임대료 중 미납금이 있을시 임대보증금에서 우선 공제한 후 잔액에 한하여 반환하며 이자는 산정치 아니한다.`,
        article10: `제10조 (임차물의 명도)

을이 퇴거할 때에는 시설 및 비품 등에 대하여 갑이 인정할 수 있는 입주시의 상태로 원상 복구하여 갑에게 명도하여야 한다. 다만, 원상복구를 하지 않았을 경우나 입주가 불가할 정도의 상태일 경우 갑이 임의로 원상복구 할 수 있으며 그 비용을 을이 부담하지 않을 시 임대보증금에서 변제한다.`,
        article11: `제11조 (분쟁의 해결)

①본 계약과 관련하여 계약당사자간에 발생하는 이의 또는 분쟁 등을 신의성실의 원칙에 입각하여 상호 협의에 의하여 해결하도록 노력한다.

②제1항에 의한 협의가 이루어지지 아니할 때에는 상호합의를 통하여 "갑"의 사업장 소재지 관할법원의 판결 또는 「중재법」에 의한 중재에 따라 해결하되, 상호합의가 이뤄지지 않을 경우 "갑"이 지정하는 방법으로 한다.`,
        footer: `이 계약을 증명하기 위하여 갑과 을이 계약서를 작성 서명 날인하고 각각 1통씩 보관한다.`
      };

      const residencyUpdateData: any = {
        // 새로운 계약 종료일로 업데이트
        occupancyPeriod: {
          startDate: room.occupancyPeriod?.startDate ? Timestamp.fromDate(room.occupancyPeriod.startDate) : Timestamp.now(),
          endDate: Timestamp.fromDate(extensionEndDate)
        },
        residentInfo: contractorInfo, // 업데이트된 계약자 정보
        updatedAt: now
      };

      if (extensionRound === 2) {
        residencyUpdateData.secondExtensionSignatureName = signatureName;
        residencyUpdateData.secondExtensionSignatureDate = Timestamp.fromDate(new Date(signatureDate));
        residencyUpdateData.secondExtensionSignatureUrl = signatureUrl;
        residencyUpdateData.secondExtensionContractSignedAt = now;
        residencyUpdateData.secondExtensionContractContent = extensionContractContent;
        residencyUpdateData.secondOriginalEndDate = room.secondOriginalEndDate
          ? Timestamp.fromDate(room.secondOriginalEndDate)
          : Timestamp.fromDate(previousEndDate);
        residencyUpdateData.secondExtensionStatus = 'contracted';
        residencyUpdateData.hasSecondExtended = true;
      } else {
        residencyUpdateData.extensionSignatureName = signatureName;
        residencyUpdateData.extensionSignatureDate = Timestamp.fromDate(new Date(signatureDate));
        residencyUpdateData.extensionSignatureUrl = signatureUrl;
        residencyUpdateData.extensionContractSignedAt = now;
        residencyUpdateData.extensionContractContent = extensionContractContent;
        residencyUpdateData.originalEndDate = room.originalEndDate
          ? Timestamp.fromDate(room.originalEndDate)
          : Timestamp.fromDate(previousEndDate);
        residencyUpdateData.extensionStatus = 'contracted';
        residencyUpdateData.hasExtended = true;
      }

      await updateDoc(doc(db, 'portville_residencies', room.currentResidencyId!), residencyUpdateData);

      // 2. 별도 연장 계약 히스토리 저장
      const extensionContractHistoryData: any = {
        roomId: roomId!,
        roomNumber: room.roomNumber,
        contractorInfo: contractorInfo,
        originalContractPeriod: {
          startDate: room.occupancyPeriod?.startDate ? Timestamp.fromDate(room.occupancyPeriod.startDate) : Timestamp.now(),
          endDate: Timestamp.fromDate(previousEndDate)
        },
        extensionPeriod: {
          startDate: Timestamp.fromDate(extensionStartDate),
          endDate: Timestamp.fromDate(extensionEndDate)
        },
        extensionSignatureName: signatureName,
        extensionSignatureDate: Timestamp.fromDate(new Date(signatureDate)),
        extensionSignatureUrl: signatureUrl,
        extensionContractSignedAt: now,
        extensionContractContent: extensionContractContent,
        type: extensionRound === 2 ? 'extension2' : 'extension',
        createdAt: now
      };

      if (room.residentUid) {
        extensionContractHistoryData.contractorUid = room.residentUid;
      }

      await addDoc(collection(db, 'portville_contracts'), extensionContractHistoryData);

      alert(
        `${extensionRound}차 연장 계약서 날인이 완료되었습니다.\n연장 기간: ${extensionStartDate.toLocaleDateString('ko-KR')} ~ ${extensionEndDate.toLocaleDateString('ko-KR')} (2년)`
      );
      goToRoomsList();
      
    } catch (error) {
      console.error('연장 계약 처리 오류:', error);
      alert('연장 계약 처리 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">로딩 중...</div>
      </div>
    );
  }

  if (!hasAccess) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">호실 정보를 불러오는 중...</div>
      </div>
    );
  }

  if (error || !room) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-800">{error || '호실을 찾을 수 없습니다.'}</div>
          <button
            onClick={goToRoomsList}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            호실 관리로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  const contractStartDate = room.occupancyPeriod?.endDate ? new Date(room.occupancyPeriod.endDate) : new Date();
  contractStartDate.setDate(contractStartDate.getDate() + 1);
  const contractEndDate = calculateTwoYearMinusOneEndDate(contractStartDate);

	  return (
	    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
	      <div className="mb-8">
	        <h1 className="text-3xl font-bold text-gray-900">포트빌 {extensionRound}차 계약 연장 날인</h1>
	        <p className="mt-2 text-sm text-gray-600">
	          {room.roomNumber}호의 {extensionRound}차 계약 연장 날인을 진행해주세요.
	        </p>
        <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-md">
          <div className="flex">
            <svg className="h-5 w-5 text-green-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div className="ml-3">
	              <p className="text-sm text-green-800">
	                계약 연장이 승인되었습니다. 연장 계약서 날인을 진행해주세요.
	                (승인일:{' '}
	                {(extensionRound === 2
	                  ? room.secondExtensionApprovedAt
	                  : room.extensionApprovedAt
	                )?.toLocaleDateString('ko-KR')}
	                )
	              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        {/* 계약서 내용 */}
        <div className="p-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900">부산 포트빌 임대차계약서</h2>
            <p className="text-gray-600 mt-2">호실번호: {room.roomNumber}호</p>
          </div>

          <div className="space-y-6 text-sm">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                임대인 부산항만공사 사장 (이하 "갑" 이라 한다) 과 임차인 {room.currentResident?.name} (이하 "을" 이라 한다) 은 아래와 같이 임대차 계약을 체결하고 신의와 성실의 원칙에 입각하여 이를 이행한다.
              </h3>
              <p className="text-gray-700 leading-relaxed">
                소재지 : 부산광역시 강서구 신항로 96-87 (성북동 1501번지)<br/>
                임대시설물: 부산 포트빌 {room.roomNumber}호 (전용 26.5㎡ 1세대)
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">제1조 (목적)</h3>
              <p className="text-gray-700 leading-relaxed">
                갑은 위 표시 부산 포트빌 각 호를 을에게 임대하고 을은 이를 임차한다. <br/>
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">제2조 (임대보증금 및 임대기간)</h3>
              <p className="text-gray-700 leading-relaxed">
                ①임대보증금 및 임대료, 임대기간을 다음과 같이 약정한다.<br/>
              </p>
              <table className="w-full border-collapse border border-gray-400 my-4">
                <tbody>
                  <tr>
                    <td className="border border-gray-400 px-4 py-2">임대보증금</td>
                    <td className="border border-gray-400 px-4 py-2">월임대료</td>
                    <td className="border border-gray-400 px-4 py-2">임대기간</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-400 px-4 py-2">800,000원</td>
                    <td className="border border-gray-400 px-4 py-2">135,000원</td>
                    <td className="border border-gray-400 px-4 py-2">
                      {contractStartDate.toLocaleDateString('ko-KR')} ~ {contractEndDate.toLocaleDateString('ko-KR')}
                    </td>
                  </tr>
                </tbody>
              </table>
              <p className="text-gray-700 leading-relaxed">
                ②임대보증금은 임대계약 체결 후 가상계좌 발급 시 을이 갑에게 납부하여야 한다.<br/>
                (신한은행 140-014-896398 예금주 : (주)아스타아이비에스 )<br/><br/>
                ③을은 임대료를 매월 말일까지 갑이 지정하는 은행에 선납하여야 한다.<br/>
                (신한은행 140-014-896099 예금주 : (주)아스타아이비에스 )<br/><br/>
                ④입주자는 열쇠지급일이 기산점이다. 입주 당월 임대료 및 관리비는 일할 계산된다.<br/><br/>
                ⑤임대기간 종료 전 을의 귀책사유로 인한 계약 해지 시, 을은 위약금으로 270,000원을 납부 또는 임대보증금에서 공제한다. 위약금의 납부·공제는 갑의 을에 대한 손해배상의 청구에 영향을 미치지 아니한다.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">제3조 (관리비)</h3>
              <p className="text-gray-700 leading-relaxed">
                ①관리비는 일반관리비(청소, 경비, 영선 등 입주자 보호 및 건물유지보수비용)와 공공요금(전기료, 수도료, TV시청료, 환경개선분담금 등)을 말한다.<br/><br/>
                ②매월 을은 일반관리비(세대당 86,500원, 부가세별도), 통신비(신청자에 한함) 및 공공요금을 납부하여야 한다.<br/><br/>
                ③을은 갑이 매월 사용량 등에 따라 산정 고지한 금액의 관리비를 매월 말일까지 납부하여야 한다. (신한 140-014-896099 예금주: (주)아스타아이비에스)<br/><br/>
                ④을이 계약기간 중 임대시설물을 사용하지 않을 경우 계약전력에 의한 기본요금을 납부하여야 한다.<br/><br/>
                ⑤관리비는 갑이 지정한 시설관리사업자가 갑을 대신하여 산정·고지할 수 있으며, 이 경우 관리비는 갑이 지정한 시설관리사업자에게 납부하여야 한다.<br/><br/>
                ⑥을은 입주 당월에 발생하는 관리비 선납부를 위하여 선관리비(100,000원)를 관리주체에 납부 예치하여야 하며, 퇴거 시 익월 말 공과금 및 시설물 보수비 등을 정산하고 잔액을 환불하며 이자는 산정치 아니한다. 다만 제7조에 따른 계약갱신 시에는 최초 납부한 선관리비는 그대로 예치한다.<br/>
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">제4조 (연체료)</h3>
              <p className="text-gray-700 leading-relaxed">
                ①을이 임대차계약에 의거 갑에게 납부하여야 할 임대료 및 관리비를 소정기일까지 납부하지 않을 때에는 국유재산법을 준용하여 가산금을 징수한다.<br/><br/>
                ②체납임대료 납부 시에는 연체료를 우선 변제하며, 체납임대료는 완납 시까지 연체료를 가산하여 납부하여야 한다.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">제5조 (임대료 및 관리비 등 조정)</h3>
              <p className="text-gray-700 leading-relaxed">
                갑은 물가상승 및 기타 경제적 여건에 따라 임대료 및 관리비를 조정할 수 있으며 조정할 때에는 을에게 사전 통보하여야 한다.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">제6조 (의무·책임)</h3>
              <p className="text-gray-700 leading-relaxed">
                ①갑은 이 계약서 내용을 성실히 이행할 의무가 있다.<br/><br/>
                ②을은 세대내 모든 시설물을 주의하여 사용하여야하며, 사용부주의로 인한 파손, 망실 시에는 변상 또는 원상복구 하여야한다.<br/><br/>
                ③을의 귀책사유로 인하여 갑 또는 제3자에게 인적 또는 물적 손해를 입혔을 때에는 을은 이를 변상함은 물론 민·형사상 모든 책임을 진다.<br/><br/>
                ④을은 입주 시 입주자 관리카드를 제출하여야 하며 변경사항이 있을시 에는 관리실에 입주자 변경사항을 알려야 한다.<br/><br/>
                ⑤을은 관리지침 및 포트빌 관리규약에 따라 임대시설을 이용해야 하며, 본 계약 및 지침, 규약을 이행하지 않을 경우 전적인 책임을 진다.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">제7조 (계약갱신)</h3>
              <p className="text-gray-700 leading-relaxed">
                을은 계약기간 만료 후 계속 거주를 희망하는 경우 만료 1개월 전에 갑에게 임대차계약의 갱신을 신청하여야 하며, 총 4년의 범위 내에서 2년 단위로 계약을 갱신할 수 있다. 단, 1회 연장 계약 후 계약 갱신은 입주대기자가 없는 경우 추가 갱신이 가능하다.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">제8조 (계약해지)</h3>
              <p className="text-gray-700 leading-relaxed">
                ①갑은 을이 다음 각 호의 1에 해당하는 경우 임대차계약을 해지할 수 있다. <br/>
                1. 허가 없이 시설물구조를 변경하거나 건물 또는 시설물을 고의적으로 훼손한 세대<br/>
                2. 임대료 및 관리비등을 3개월 이상 체납한 세대 <br/>
                4. 임대보증금의 반환청구권이 제3채권자의 질권 기타 채권담보의 목적이 되었을 때 <br/>
                5. 질병 및 법정 전염병에 감염되어 타인에게 전염될 염려가 있는 자 <br/>
                6. 음주 및 가무 등으로 풍기를 문란하게 하여 퇴거 경고를 3회 받은 세대 <br/>
                7. 시설 내 질서와 안녕을 해칠 우려가 있어 퇴거 경고를 3회 받은 세대 <br/>
                8. 기타 입주자격이 부적당하다고 갑이 인정한 세대 <br/>
                9. 갑의 동의 없이 제3자에게 임대시설물 전부 또는 일부를 전대한 세대 <br/>
                ②갑, 을이 어느 일방의 사정으로 계약 해지하고자 할 때에는 1개월 이전에 상대방에게 통보하여야 한다. <br/>
                ③을은 임대차계약 해지 신청 시 미납금 및 고지 예정인 관리비를 정산하여야 한다.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">제9조 (보증금의 반환)</h3>
              <p className="text-gray-700 leading-relaxed">
                을이 갑에게 예치한 임대보증금은 퇴거 시에(공휴일인 경우 익영업일)에 반환한다. 이때 을이 갑에게 납부하여야 할 월 임대료 중 미납금이 있을시 임대보증금에서 우선 공제한 후 잔액에 한하여 반환하며 이자는 산정치 아니한다.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">제10조 (임차물의 명도)</h3>
              <p className="text-gray-700 leading-relaxed">
                을이 퇴거할 때에는 시설 및 비품 등에 대하여 갑이 인정할 수 있는 입주시의 상태로 원상 복구하여 갑에게 명도하여야 한다. 다만, 원상복구를 하지 않았을 경우나 입주가 불가할 정도의 상태일 경우 갑이 임의로 원상복구 할 수 있으며 그 비용을 을이 부담하지 않을 시 임대보증금에서 변제한다.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">제11조 (분쟁의 해결)</h3>
              <p className="text-gray-700 leading-relaxed">
                ①본 계약과 관련하여 계약당사자간에 발생하는 이의 또는 분쟁 등을 신의성실의 원칙에 입각하여 상호 협의에 의하여 해결하도록 노력한다. <br/>
                ②제1항에 의한 협의가 이루어지지 아니할 때에는 상호합의를 통하여 “갑”의 사업장 소재지 관할법원의 판결 또는 「중재법」에 의한 중재에 따라 해결하되, 상호합의가 이뤄지지 않을 경우 “갑”이 지정하는 방법으로 한다. <br/>
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                이 계약을 증명하기 위하여 갑과 을이 계약서를 작성 서명 날인하고 각각 1통씩 보관한다.
              </h3>
            </div>
          </div>

          {/* 연장 계약 정보 요약 */}
          <div className="mb-8 p-4 bg-green-50 border border-green-200 rounded-md">
            <h3 className="font-semibold text-green-900 mb-3">연장 계약 정보 요약</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-green-700">계약자:</span>
                <span className="ml-2">{room.currentResident?.name}</span>
              </div>
              <div>
                <span className="font-medium text-green-700">연락처:</span>
                <span className="ml-2">{room.currentResident?.phoneNumber}</span>
              </div>
              <div>
                <span className="font-medium text-green-700">소속:</span>
                <span className="ml-2">{room.currentResident?.organization}</span>
              </div>
              <div>
                <span className="font-medium text-green-700">호실번호:</span>
                <span className="ml-2">{room.roomNumber}호</span>
              </div>
              <div>
                <span className="font-medium text-green-700">기존 계약 종료일:</span>
                <span className="ml-2">{room.occupancyPeriod?.endDate.toLocaleDateString('ko-KR')}</span>
              </div>
              <div>
                <span className="font-medium text-green-700">연장 후 종료일:</span>
                <span className="ml-2 text-green-600 font-semibold">
                  {(() => {
                    const prevEnd = room.occupancyPeriod?.endDate || new Date();
                    const start = new Date(prevEnd);
                    start.setDate(start.getDate() + 1);
                    const end = calculateTwoYearMinusOneEndDate(start);
                    return end.toLocaleDateString('ko-KR');
                  })()}
                </span>
              </div>
            </div>
          </div>

          {/* 서명 영역 */}
          <div className="mt-12 border-t pt-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* 서명자 정보 입력 */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-4">서명자 정보</h3>
	                <div className="space-y-4">
	                  <div>
	                    <label className="block text-sm font-medium text-gray-700 mb-2">
	                      서명자 성명
	                    </label>
	                    <input
	                      type="text"
	                      value={signatureName}
	                      onChange={(e) => setSignatureName(e.target.value)}
	                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
	                      placeholder="서명자 이름을 입력하세요"
	                    />
	                  </div>
	                  <div>
	                    <label className="block text-sm font-medium text-gray-700 mb-2">
	                      서명자 주소
	                    </label>
	                    <input
	                      type="text"
	                      value={signatureAddress}
	                      onChange={(e) => setSignatureAddress(e.target.value)}
	                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
	                      placeholder="주소를 입력하세요"
	                    />
	                  </div>
	                  <div>
	                    <label className="block text-sm font-medium text-gray-700 mb-2">
	                      서명자 주민등록번호
	                    </label>
                      <div className="flex items-center gap-2">
	                    <input
	                      type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        maxLength={6}
	                      value={signatureResidentNumberFront}
	                      onChange={(e) => setSignatureResidentNumberFront(sanitizeResidentNumberPart(e.target.value, 6))}
	                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
	                      placeholder="앞 6자리"
	                    />
                      <span className="text-gray-500">-</span>
	                    <input
	                      type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        maxLength={7}
	                      value={signatureResidentNumberBack}
	                      onChange={(e) => setSignatureResidentNumberBack(sanitizeResidentNumberPart(e.target.value, 7))}
	                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
	                      placeholder="뒤 7자리"
	                    />
                      </div>
	                    <p className="mt-1 text-xs text-gray-500">
	                      숫자만 입력하면 됩니다. 저장 시에는 000000-0000000 형식으로 통일됩니다.
	                    </p>
	                  </div>
	                  <div>
	                    <label className="block text-sm font-medium text-gray-700 mb-2">
	                      서명 날짜
	                    </label>
	                    <input
                      type="date"
                      value={signatureDate}
                      onChange={(e) => setSignatureDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                </div>
              </div>

              {/* 서명 패드 */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-4">서명</h3>
                <div 
                  className="border-2 border-gray-300 rounded-md overflow-hidden bg-white"
                  style={{ 
                    width: `${signatureSize.width}px`, 
                    height: `${signatureSize.height}px`,
                    maxWidth: '100%'
                  }}
                >
                  <SignatureCanvas
                    ref={sigCanvasRef}
                    canvasProps={{
                      width: signatureSize.width,
                      height: signatureSize.height,
                      className: 'signature-canvas',
                      style: { 
                        width: '100%', 
                        height: '100%',
                        display: 'block'
                      }
                    }}
                    backgroundColor="white"
                  />
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  <button
                    onClick={clearSignature}
                    className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                  >
                    서명 지우기
                  </button>
                  <span className="text-xs text-gray-500 self-center">
                    서명 영역: {signatureSize.width}×{signatureSize.height}px
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* 계약 완료 버튼 */}
          <div className="mt-8 flex justify-end space-x-4">
            <button
              onClick={goToRoomsList}
              className="px-6 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              disabled={saving}
            >
              취소
            </button>
            <button
              onClick={saveExtensionContract}
              disabled={saving}
              className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? '처리 중...' : '연장 계약서 날인 완료'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExtensionContractPage; 
